import { useFeedbackStore } from "../features/support-feedback/store.js";
import { createFeedbackService } from "../features/support-feedback/service.js";
import { createProductMappingChecksService } from "../features/products/product-mapping-checks.js";
import { useProductMappingChecksStore } from "../features/products/product-mapping-checks-store.js";
import { createProductBulkMonitoringService } from "../features/products/product-bulk-monitoring.js";
import { useProductBulkMonitoringStore } from "../features/products/product-bulk-monitoring-store.js";
import { createCatalogRepairService } from "../features/products/catalog-repair.js";
import { useCatalogRepairStore } from "../features/products/catalog-repair-store.js";
import { createEligibilityResetService } from "../features/products/eligibility-reset.js";
import { useEligibilityResetStore } from "../features/products/eligibility-reset-store.js";
import { createApp } from "vue";
import { createPinia } from "pinia";
import type { Router } from "vue-router";

import { createProductBulkPolicyService } from "../features/policies/product-bulk-policy.js";
import { useProductBulkPolicyStore } from "../features/policies/product-bulk-policy-store.js";

import App from "../App.vue";
import { createMerchantApiClient } from "../api-client.js";
import { createAuditService } from "../features/audit/service.js";
import { useAuditStore } from "../features/audit/store.js";
import { createMerchantBootstrapService } from "../features/authentication/service.js";
import { createMerchantDashboardService } from "../features/dashboard/service.js";
import { useDashboardStore } from "../features/dashboard/store.js";
import { createMerchantOnboardingService } from "../features/onboarding/service.js";
import { useOnboardingStore } from "../features/onboarding/store.js";
import { createMerchantRequestsService } from "../features/requests/service.js";
import { useRequestsStore } from "../features/requests/store.js";
import { createProductsService } from "../features/products/service.js";
import { useProductsStore } from "../features/products/store.js";
import { createCompetitorsService } from "../features/competitors/service.js";
import { useCompetitorsStore } from "../features/competitors/store.js";
import { createPoliciesService } from "../features/policies/service.js";
import { usePoliciesStore } from "../features/policies/store.js";
import { createMappingsService } from "../features/mappings/service.js";
import { useMappingsStore } from "../features/mappings/store.js";
import { createMonitoringService } from "../features/monitoring/service.js";
import { useMonitoringStore } from "../features/monitoring/store.js";
import { createNotificationsService } from "../features/notifications/service.js";
import { useNotificationsStore } from "../features/notifications/store.js";
import { createStoreSettingsService } from "../features/store-settings/service.js";
import { useStoreSettingsStore } from "../features/store-settings/store.js";
import { createBillingService } from "../features/billing/service.js";
import { useBillingStore } from "../features/billing/store.js";
import { createTeamAccessService } from "../features/team-access/service.js";
import { useTeamAccessStore } from "../features/team-access/store.js";
import { createWidgetSettingsService } from "../features/widget-settings/service.js";
import { useWidgetSettingsStore } from "../features/widget-settings/store.js";
import { createAnalyticsService } from "../features/analytics/service.js";
import { useAnalyticsStore } from "../features/analytics/store.js";
import { useMerchantSessionStore } from "../stores/session-store.js";
import { readMerchantRuntimeConfig } from "./config.js";
import { initializeEmbeddedAppBridge } from "./embedded-app-bridge.js";

export interface MerchantAppOptions {
  rootSelector: string;
  router: Router;
}

export function mountMerchantApplication(options: MerchantAppOptions): void {
  const root = document.querySelector<HTMLElement>(options.rootSelector);
  if (root === null) return;

  const app = createApp(App);
  const pinia = createPinia();
  app.use(pinia);
  app.use(options.router);
  installBootstrapDependencies();
  app.mount(root);
}

function installBootstrapDependencies(): void {
  const config = readMerchantRuntimeConfig();
  const appBridge = initializeEmbeddedAppBridge(config);
  const apiClient = createMerchantApiClient({ baseUrl: config.apiBaseUrl, appBridge });
  useFeedbackStore().configure(createFeedbackService(apiClient));
  const audit = useAuditStore();
  const session = useMerchantSessionStore();
  const onboarding = useOnboardingStore();
  const dashboard = useDashboardStore();
  const requests = useRequestsStore();
  const products = useProductsStore();
  const competitors = useCompetitorsStore();
  const policies = usePoliciesStore();
  const mappings = useMappingsStore();
  const monitoring = useMonitoringStore();
  const notifications = useNotificationsStore();
  const storeSettings = useStoreSettingsStore();
  const billing = useBillingStore();
  const teamAccess = useTeamAccessStore();
  const widgetSettings = useWidgetSettingsStore();
  const analytics = useAnalyticsStore();
  audit.configure(createAuditService({ apiClient }));
  session.configure(createMerchantBootstrapService({ apiClient }));
  onboarding.configure(createMerchantOnboardingService({ apiClient }));
  dashboard.configure(createMerchantDashboardService({ apiClient }));
  requests.configure(createMerchantRequestsService({ apiClient }));
  products.configure(createProductsService({ apiClient }));
  useEligibilityResetStore().configure(createEligibilityResetService(apiClient));
  useCatalogRepairStore().configure(createCatalogRepairService(apiClient));
  useProductMappingChecksStore().configure(createProductMappingChecksService(apiClient));
  useProductBulkMonitoringStore().configure(createProductBulkMonitoringService(apiClient));
  competitors.configure(createCompetitorsService({ apiClient }));
  const policyService = createPoliciesService({ apiClient });
  policies.configure(policyService);
  useProductBulkPolicyStore().configure(createProductBulkPolicyService(policyService));
  mappings.configure(createMappingsService({ apiClient }));
  monitoring.configure(createMonitoringService({ apiClient }));
  notifications.configure(createNotificationsService({ apiClient }));
  storeSettings.configure(createStoreSettingsService({ apiClient }));
  billing.configure(createBillingService(apiClient));
  teamAccess.configure(createTeamAccessService({ apiClient }));
  widgetSettings.configure(createWidgetSettingsService({ apiClient }));
  analytics.configure(createAnalyticsService({ apiClient }));
}
